import { useId, type CSSProperties } from "react";
import { preload } from "react-dom";
import "../styles/finn.css";

/** One reaction per moment in the recruiting flow; see app/styles/finn.css. */
export type FinnState =
  | "idle"
  | "hello"
  | "think"
  | "search"
  | "write"
  | "success"
  | "mail"
  | "interview"
  | "match"
  | "oops"
  | "sleep";

export interface FinnProps {
  state?: FinnState;
  /** Preload the body render — set on above-the-fold (LCP) placements. */
  priority?: boolean;
  /** Logo-sized: crops to the body and uses an 8 KB render; effects don't fit. */
  mark?: boolean;
  className?: string;
}

const SRC = "/images/finn";
// The full stage leaves room above and right for effects and accessories. The
// mark crops to the body: mark.webp is body.webp cut to its alpha bounds, so
// it sits at those bounds in rig units and the face lines up unchanged.
const FRAMES = {
  full: {
    viewBox: "-60 -260 1560 1460",
    src: "body.webp",
    x: 0,
    y: 0,
    width: 1351,
    height: 1164,
  },
  mark: {
    viewBox: "208 30 952 1100",
    src: "mark.webp",
    x: 228.2,
    y: 70.6,
    width: 911.1,
    height: 1049.8,
  },
};
const HEART = "M0 24 C-44 -4 -32 -44 0 -20 C32 -44 44 -4 0 24Z";

// Confetti flight path, read by the finn-burst keyframes.
const burst = (dx: number, dy: number, r: number) =>
  ({ "--dx": `${dx}px`, "--dy": `${dy}px`, "--r": `${r}deg` }) as CSSProperties;

interface EyeProps {
  u: string;
  k: number;
  cx: number;
  cy: number;
  rx: number;
  ry: number;
}

function Eye({ u, k, cx, cy, rx, ry }: EyeProps) {
  const prx = rx * 0.42;
  const pry = ry * 0.74;
  const px = cx + rx - prx * 0.92;
  const py = cy - ry * 0.05;
  return (
    <g>
      <ellipse
        cx={cx}
        cy={cy + 1}
        rx={rx + 3.5}
        ry={ry + 3.5}
        fill="#020B3D"
        opacity=".55"
        filter={`url(#${u}soft2)`}
      />
      <g className="ball">
        <clipPath id={`${u}c${k}`}>
          <ellipse cx={cx} cy={cy} rx={rx} ry={ry} />
        </clipPath>
        <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill={`url(#${u}scl)`} />
        <g clipPath={`url(#${u}c${k})`}>
          <g className="pupil">
            <ellipse
              cx={px - 3}
              cy={py}
              rx={prx + 1.5}
              ry={pry + 1}
              fill="#F4F1EC"
            />
            <ellipse cx={px} cy={py} rx={prx} ry={pry} fill={`url(#${u}pup)`} />
            <ellipse
              cx={px}
              cy={py - pry * 0.5}
              rx={prx * 0.78}
              ry={pry * 0.38}
              fill={`url(#${u}sheen)`}
            />
            <path
              d={`M${px - prx * 0.62} ${py - pry * 0.55} Q${px - prx * 0.2} ${py - pry * 0.92} ${px + prx * 0.35} ${py - pry * 0.8}`}
              fill="none"
              stroke="#fff"
              strokeWidth="2.2"
              strokeLinecap="round"
              opacity=".55"
            />
          </g>
          <g className="heart-eye">
            <g transform={`translate(${cx + 6} ${cy + 2}) scale(.82)`}>
              <path d={HEART} fill="#F4F1EC" transform="translate(-3 0)" />
              <path className="hp" d={HEART} fill={`url(#${u}hrt)`} />
            </g>
          </g>
          <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill={`url(#${u}edge)`} />
          <ellipse
            cx={cx - rx * 0.32}
            cy={cy - ry * 0.42}
            rx={rx * 0.34}
            ry={ry * 0.16}
            fill="#fff"
            opacity=".45"
            filter={`url(#${u}soft2)`}
            transform={`rotate(-24 ${cx - rx * 0.32} ${cy - ry * 0.42})`}
          />
        </g>
      </g>
    </g>
  );
}

/**
 * Finn, the heyFinn mascot: the clay body render with an animated SVG face.
 * Accessory renders (lens, pad, envelope, calendar) load only for the state that shows them.
 */
export function Finn({
  state = "idle",
  priority = false,
  mark = false,
  className,
}: FinnProps) {
  // Gradient/filter ids must be unique per instance; strip React's id punctuation for url(#…).
  const u = `finn${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const frame = FRAMES[mark ? "mark" : "full"];
  if (priority)
    preload(`${SRC}/${frame.src}`, { as: "image", fetchPriority: "high" });
  const clay = `url(#${u}clay)`;

  return (
    <svg
      className={className ? `finn ${className}` : "finn"}
      data-state={state}
      viewBox={frame.viewBox}
      role="img"
      aria-label="Finn"
    >
      <defs>
        <radialGradient id={`${u}scl`} cx="56%" cy="40%" r="62%">
          <stop offset="0" stopColor="#FBF0E1" />
          <stop offset=".55" stopColor="#F7E9D8" />
          <stop offset=".86" stopColor="#EBD9C3" />
          <stop offset="1" stopColor="#D6C0A4" />
        </radialGradient>
        <radialGradient id={`${u}edge`} cx="50%" cy="50%" r="50%">
          <stop offset=".86" stopColor="#3B2A18" stopOpacity="0" />
          <stop offset="1" stopColor="#3B2A18" stopOpacity=".22" />
        </radialGradient>
        <linearGradient id={`${u}pup`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#4A5690" />
          <stop offset=".3" stopColor="#121B52" />
          <stop offset=".62" stopColor="#030C2E" />
          <stop offset="1" stopColor="#0E1846" />
        </linearGradient>
        <radialGradient id={`${u}sheen`} cx="45%" cy="40%" r="60%">
          <stop offset="0" stopColor="#C9D3FF" stopOpacity=".42" />
          <stop offset="1" stopColor="#C9D3FF" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${u}hrt`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FF8FB2" />
          <stop offset=".45" stopColor="#F23A73" />
          <stop offset="1" stopColor="#C21452" />
        </linearGradient>
        <radialGradient id={`${u}mo`} cx="40%" cy="30%" r="80%">
          <stop offset="0" stopColor="#2C3780" />
          <stop offset=".6" stopColor="#0C1240" />
          <stop offset="1" stopColor="#050820" />
        </radialGradient>
        <filter id={`${u}soft`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
        <filter id={`${u}soft2`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" />
        </filter>
        <filter id={`${u}sh`} x="-50%" y="-300%" width="200%" height="700%">
          <feGaussianBlur stdDeviation="22" />
        </filter>
        <filter
          id={`${u}clay`}
          x="-40%"
          y="-40%"
          width="180%"
          height="190%"
          colorInterpolationFilters="sRGB"
        >
          <feGaussianBlur in="SourceAlpha" stdDeviation="7" result="b" />
          <feDiffuseLighting
            in="b"
            surfaceScale="6"
            diffuseConstant="1.12"
            lightingColor="#fff"
            result="d"
          >
            <feDistantLight azimuth="225" elevation="55" />
          </feDiffuseLighting>
          <feSpecularLighting
            in="b"
            surfaceScale="6"
            specularConstant=".6"
            specularExponent="20"
            lightingColor="#fff"
            result="s"
          >
            <feDistantLight azimuth="225" elevation="62" />
          </feSpecularLighting>
          <feComposite
            in="SourceGraphic"
            in2="d"
            operator="arithmetic"
            k1="1.1"
            result="lit"
          />
          <feComposite in="s" in2="SourceAlpha" operator="in" result="s2" />
          <feComposite
            in="lit"
            in2="s2"
            operator="arithmetic"
            k2="1"
            k3=".5"
            result="all"
          />
          <feComposite in="all" in2="SourceAlpha" operator="in" result="obj" />
          <feGaussianBlur in="SourceAlpha" stdDeviation="8" />
          <feOffset dx="5" dy="14" result="so" />
          <feFlood floodColor="#0A1250" floodOpacity=".22" />
          <feComposite in2="so" operator="in" result="shadow" />
          <feMerge>
            <feMergeNode in="shadow" />
            <feMergeNode in="obj" />
          </feMerge>
        </filter>
      </defs>
      {!mark && (
        <ellipse
          className="shadow"
          cx="540"
          cy="1118"
          rx="300"
          ry="34"
          filter={`url(#${u}sh)`}
        />
      )}

      {state === "interview" && (
        <g className="bob">
          <image
            href={`${SRC}/cal.webp`}
            x="975"
            y="610"
            width="400"
            height="400"
          />
        </g>
      )}
      {state === "write" && (
        <g className="doc">
          <g className="bob slow">
            <g transform="translate(985 600) scale(1.25) translate(-985 -600)">
              <image
                href={`${SRC}/pad.webp`}
                x="985"
                y="600"
                width="320"
                height="320"
              />
              <g transform="rotate(3.5 1145 760)" filter={clay}>
                <rect
                  className="ln"
                  x="1050"
                  y="728"
                  width="120"
                  height="20"
                  rx="10"
                  fill="#1F4BEA"
                />
                <rect
                  className="ln"
                  x="1050"
                  y="768"
                  width="170"
                  height="15"
                  rx="7.5"
                  fill="#1B2565"
                />
                <rect
                  className="ln"
                  x="1050"
                  y="800"
                  width="140"
                  height="15"
                  rx="7.5"
                  fill="#1B2565"
                />
                <rect
                  className="ln"
                  x="1050"
                  y="832"
                  width="96"
                  height="15"
                  rx="7.5"
                  fill="#1B2565"
                />
              </g>
            </g>
          </g>
        </g>
      )}

      <g className="rig">
        <g className="lean">
          <image
            href={`${SRC}/${frame.src}`}
            x={frame.x}
            y={frame.y}
            width={frame.width}
            height={frame.height}
          />
          <g className="face">
            <g className="eyes-open">
              <Eye u={u} k={1} cx={860} cy={314} rx={53} ry={55} />
              <Eye u={u} k={2} cx={990} cy={292} rx={51} ry={56} />
            </g>
            <g className="eyes-happy">
              <path className="ink" d="M816 326 Q860 278 904 320" />
              <path className="ink" d="M948 304 Q990 256 1034 298" />
            </g>
            <g className="eyes-closed">
              <path className="ink" d="M818 312 Q860 342 902 308" />
              <path className="ink" d="M950 290 Q990 320 1032 286" />
            </g>
            <g
              className="blush"
              fill="#FF6FA3"
              opacity=".42"
              filter={`url(#${u}soft)`}
            >
              <ellipse cx="824" cy="380" rx="34" ry="16" />
              <ellipse cx="1052" cy="352" rx="30" ry="15" />
            </g>
            <path className="ink m-smile" d="M884 392 Q926 418 966 386" />
            <path
              className="m-big"
              d="M876 380 Q924 452 976 372 Q926 392 876 380Z"
              fill={`url(#${u}mo)`}
              stroke="#0A1035"
              strokeWidth="8"
              strokeLinejoin="round"
            />
            <g className="m-o">
              <ellipse
                cx="920"
                cy="400"
                rx="17"
                ry="22"
                fill={`url(#${u}mo)`}
              />
              <ellipse
                cx="914"
                cy="391"
                rx="5"
                ry="4"
                fill="#fff"
                opacity=".35"
              />
            </g>
            <path className="ink m-flat" d="M898 400 Q928 392 958 396" />
          </g>
          {state === "search" && (
            <g className="bob fast">
              <image
                href={`${SRC}/lens.webp`}
                x="915"
                y="125"
                width="330"
                height="440"
              />
            </g>
          )}
        </g>
      </g>

      {state === "mail" && (
        <g className="pop">
          <image
            href={`${SRC}/env.webp`}
            x="1040"
            y="-150"
            width="300"
            height="240"
          />
        </g>
      )}
      <g className="fx think" fill="#F3E6CF" filter={clay}>
        <circle cx="1150" cy="40" r="22" />
        <circle cx="1215" cy="-40" r="32" />
        <circle cx="1300" cy="-140" r="46" />
      </g>
      <g className="fx hearts" filter={clay}>
        <g transform="translate(1180 80) scale(1.3)">
          <path className="hp" d={HEART} fill="#FF4D84" />
        </g>
        <g transform="translate(760 40)">
          <path className="hp" d={HEART} fill="#FF7AA5" />
        </g>
        <g transform="translate(1010 -40) scale(.9)">
          <path className="hp" d={HEART} fill="#FF4D84" />
        </g>
      </g>
      <g className="fx sweat" filter={clay}>
        <path d="M1170 110 q-34 48 0 62 q34 -14 0 -62z" fill="#8CC8FF" />
      </g>
      <g className="fx zzz" filter={clay}>
        <text x="1150" y="60">
          Z
        </text>
        <text x="1235" y="-20">
          z
        </text>
        <text x="1295" y="-90">
          z
        </text>
      </g>
      <g className="fx confetti" filter={clay}>
        <rect
          x="760"
          y="40"
          width="40"
          height="24"
          rx="10"
          fill="#FFC531"
          style={burst(-400, -260, 220)}
        />
        <circle
          cx="778"
          cy="50"
          r="17"
          fill="#1F4BEA"
          style={burst(-200, -400, 0)}
        />
        <rect
          x="760"
          y="40"
          width="40"
          height="24"
          rx="10"
          fill="#FF7AA5"
          style={burst(40, -430, -180)}
        />
        <circle
          cx="778"
          cy="50"
          r="17"
          fill="#F3E6CF"
          style={burst(280, -370, 0)}
        />
        <rect
          x="760"
          y="40"
          width="40"
          height="24"
          rx="10"
          fill="#1F4BEA"
          style={burst(500, -220, 260)}
        />
        <rect
          x="760"
          y="40"
          width="32"
          height="32"
          rx="9"
          fill="#FFC531"
          style={burst(560, 20, 140)}
        />
        <circle
          cx="778"
          cy="50"
          r="15"
          fill="#FF7AA5"
          style={burst(-480, -20, 0)}
        />
        <rect
          x="760"
          y="40"
          width="40"
          height="24"
          rx="10"
          fill="#F3E6CF"
          style={burst(-320, -460, -240)}
        />
      </g>
    </svg>
  );
}
